import { useCallback, useMemo, useState } from 'react';

import valid from 'semver/functions/valid';

import { Release } from '../models/release.model';

import { localStorageService } from '../services/local-storage.service';

import { APP_VERSION, RELEASES } from '../releases';
import {
    filterReleasesForUser,
    getUnseenReleases,
} from '../releases/releaseNotes';
import { useAuthSession } from './useAuthSession';

export const LAST_SEEN_VERSION_KEY = 'composer.lastSeenVersion';

export interface UseReleaseNotesResult {
    currentVersion: string;
    releases: Release[];
    unseenReleases: Release[];
    hasUnseen: boolean;
    lastSeenVersion: string;
    markSeen: () => void;
}

// A first visit, an unreadable value or unavailable storage all count as
// "seen up to now", so nobody is shown the whole history.
function readLastSeenVersion(currentVersion: string): string {
    try {
        const stored = localStorageService.getLocalStorageValue(
            LAST_SEEN_VERSION_KEY,
        );
        if (stored && valid(stored)) return stored;
        localStorageService.setLocalStorageValue(
            LAST_SEEN_VERSION_KEY,
            currentVersion,
        );
    } catch {
        // Storage unavailable (private mode, blocked site data).
    }
    return currentVersion;
}

export default function useReleaseNotes(
    allReleases: Release[] = RELEASES,
    currentVersion: string = APP_VERSION,
): UseReleaseNotesResult {
    const { user } = useAuthSession();
    const isBetaMode = !!user?.isBetaMode;
    const [lastSeenVersion, setLastSeenVersion] = useState(() =>
        readLastSeenVersion(currentVersion),
    );

    const releases = useMemo(
        () => filterReleasesForUser(allReleases, isBetaMode),
        [allReleases, isBetaMode],
    );
    const unseenReleases = useMemo(
        () => getUnseenReleases(releases, lastSeenVersion),
        [releases, lastSeenVersion],
    );

    const markSeen = useCallback(() => {
        try {
            localStorageService.setLocalStorageValue(
                LAST_SEEN_VERSION_KEY,
                currentVersion,
            );
        } catch {
            // Storage unavailable: still clear the badge for this session.
        }
        setLastSeenVersion(currentVersion);
    }, [currentVersion]);

    return {
        currentVersion,
        releases,
        unseenReleases,
        hasUnseen: unseenReleases.length > 0,
        lastSeenVersion,
        markSeen,
    };
}
